/**
 * 订单相关枚举
 */

import { t } from '@/i18n'

/**
 * 运输模式枚举
 * @description 运输模式：FCL/LCL
 */
export const TRANSPORT_MODE_ENUM = {
  FCL: {
    value: 'FCL',
    get label() {
      return t('orders.enum.transportMode.FCL')
    }
  },
  LCL: {
    value: 'LCL',
    get label() {
      return t('orders.enum.transportMode.LCL')
    }
  }
} as const

/**
 * 订单状态枚举
 * @description 订单状态：1-草稿 2-进行中 3-已完成 4-已取消
 */
export const ORDER_STATUS_ENUM = {
  DRAFT: {
    value: 1,
    get label() {
      return t('orders.enum.status.draft')
    }
  },
  IN_PROGRESS: {
    value: 2,
    get label() {
      return t('orders.enum.status.inProgress')
    }
  },
  COMPLETED: {
    value: 3,
    get label() {
      return t('orders.enum.status.completed')
    }
  },
  CANCELLED: {
    value: 4,
    get label() {
      return t('orders.enum.status.cancelled')
    }
  }
} as const

/**
 * 订单类型枚举
 * @description 订单类型：1-海运订单 2-空运订单 3-陆运订单 4-铁路订单
 */
export const ORDER_TYPE_ENUM = {
  SEA: {
    value: 1,
    get label() {
      return t('orders.enum.type.sea')
    }
  },
  AIR: {
    value: 2,
    get label() {
      return t('orders.enum.type.air')
    }
  },
  LAND: {
    value: 3,
    get label() {
      return t('orders.enum.type.land')
    }
  },
  RAIL: {
    value: 4,
    get label() {
      return t('orders.enum.type.rail')
    }
  }
} as const

/**
 * 订单创建类型枚举
 * @description 订单创建类型：1-需要报价 2-已有报价 3-代订舱
 */
export const ORDER_CREATE_TYPE_ENUM = {
  NEED_QUOTE: {
    value: 1,
    get label() {
      return t('orders.enum.createType.needQuote')
    },
    color: 'orange'
  },
  HAS_QUOTE: {
    value: 2,
    get label() {
      return t('orders.enum.createType.hasQuote')
    },
    color: 'blue'
  },
  BOOKING: {
    value: 3,
    get label() {
      return t('orders.enum.createType.booking')
    },
    color: 'green'
  }
} as const

/**
 * 车架号异常状态枚举
 * @description 车架号异常状态：all-全部 true-有异常 false-无异常
 */
export const CHASSIS_ABNORMAL_STATUS_ENUM = {
  ALL: {
    value: '',
    get label() {
      return t('orders.enum.chassisAbnormalStatus.all')
    }
  },
  HAS_ABNORMAL: {
    value: 'true',
    get label() {
      return t('orders.enum.chassisAbnormalStatus.hasAbnormal')
    }
  },
  NO_ABNORMAL: {
    value: 'false',
    get label() {
      return t('orders.enum.chassisAbnormalStatus.noAbnormal')
    }
  }
} as const

/**
 * 关联方类型枚举
 * @description 关联方类型：shipper-发货人 consignee-收货人 notify-通知人 notify2-通知人2
 */
export const PARTY_TYPE_ENUM = {
  SHIPPER: {
    value: 'shipper',
    get label() {
      return t('orders.enum.partyType.shipper')
    }
  },
  CONSIGNEE: {
    value: 'consignee',
    get label() {
      return t('orders.enum.partyType.consignee')
    }
  },
  NOTIFY: {
    value: 'notify',
    get label() {
      return t('orders.enum.partyType.notify')
    }
  },
  NOTIFY2: {
    value: 'notify2',
    get label() {
      return t('orders.enum.partyType.notify2')
    }
  }
} as const

/**
 * 控件类型枚举
 * @description 控件选择：1-文本框 2-下拉框
 */
export const WIDGET_TYPE_ENUM = {
  TEXTBOX: {
    value: 1,
    get label() {
      return t('orders.enum.widgetType.textbox')
    },
    code: 'textbox'
  },
  DROPDOWN: {
    value: 2,
    get label() {
      return t('orders.enum.widgetType.dropdown')
    },
    code: 'dropdown'
  }
} as const

/**
 * 订单文件业务类型枚举
 */
export const ORDER_FILE_TYPE_ENUM = {
  DRAFT_BILL_OF_LADING: {
    value: '提单草单',
    get label() {
      return t('orders.enum.orderFileType.draftBillOfLading')
    }
  },
  ORIGINAL_BILL_OF_LADING: {
    value: '提单正本',
    get label() {
      return t('orders.enum.orderFileType.originalBillOfLading')
    }
  },
  TELEX_RELEASE_GUARANTEE: {
    value: '电放保函',
    get label() {
      return t('orders.enum.orderFileType.telexReleaseGuarantee')
    }
  },
  CUSTOMS_DECLARATION: {
    value: '报关单',
    get label() {
      return t('orders.enum.orderFileType.customsDeclaration')
    }
  },
  DRAFT_CUSTOMS_DECLARATION: {
    value: '报关草单',
    get label() {
      return t('orders.enum.orderFileType.draftCustomsDeclaration')
    }
  },
  RELEASE_NOTICE: {
    value: '放行通知书',
    get label() {
      return t('orders.enum.orderFileType.releaseNotice')
    }
  },
  CUSTOMS_DECLARATION_COPY: {
    value: '报关底单',
    get label() {
      return t('orders.enum.orderFileType.customsDeclarationCopy')
    }
  },
  PRE_TAX_DOCUMENT: {
    value: '预税单',
    get label() {
      return t('orders.enum.orderFileType.preTaxDocument')
    }
  },
  OFFICIAL_TAX_DOCUMENT: {
    value: '正式税单',
    get label() {
      return t('orders.enum.orderFileType.officialTaxDocument')
    }
  },
  CARGO_RELATED: {
    value: '货物相关文件',
    get label() {
      return t('orders.enum.orderFileType.cargoRelated')
    }
  },
  CERTIFICATION_REPORT: {
    value: '认证报告',
    get label() {
      return t('orders.enum.orderFileType.certificationReport')
    }
  },
  SUPPLIER_UPLOAD: {
    value: '供应商上传文件',
    get label() {
      return t('orders.enum.orderFileType.supplierUpload')
    }
  }
} as const

/**
 * 运输委托单新增文件业务类型枚举。
 *
 * 这些类型同时用于内部文件管理和供应商确认外链，不扩展到其他订单附件下拉。
 */
export const TRANSPORT_CONSIGNMENT_ADDITIONAL_FILE_TYPE_ENUM = {
  SIX_COPY_FORM: {
    value: '六联单',
    get label() {
      return t('orders.enum.orderFileType.sixCopyForm')
    }
  },
  CUSTOMS_TRANSIT_ADVANCE_RELEASE_FORM: {
    value: '转关提前验放单',
    get label() {
      return t('orders.enum.orderFileType.customsTransitAdvanceReleaseForm')
    }
  },
  CUSTOMS_TRANSIT_ADVANCE_DECLARATION_FORM: {
    value: '转关提前申报单',
    get label() {
      return t('orders.enum.orderFileType.customsTransitAdvanceDeclarationForm')
    }
  }
} as const


// ============================= 工具函数 =============================

/**
 * 获取订单创建类型的颜色
 */
export const getOrderCreateTypeColor = (type: number): string => {
  const enumItem = Object.values(ORDER_CREATE_TYPE_ENUM).find(item => item.value === type)
  return enumItem?.color || 'gray'
}

/**
 * 获取订单创建类型的标签
 */
export const getOrderCreateTypeLabel = (type: number): string => {
  const enumItem = Object.values(ORDER_CREATE_TYPE_ENUM).find(item => item.value === type)
  return enumItem?.label || `${t('orders.messages.typePrefix')}${type}`
}

/**
 * 获取订单状态的标签
 */
export const getOrderStatusLabel = (status: number): string => {
  const enumItem = Object.values(ORDER_STATUS_ENUM).find(item => item.value === status)
  return enumItem?.label || `${t('orders.messages.statusPrefix')}${status}`
}

/**
 * 获取订单类型的标签
 */
export const getOrderTypeLabel = (type: number): string => {
  const enumItem = Object.values(ORDER_TYPE_ENUM).find(item => item.value === type)
  return enumItem?.label || `${t('orders.messages.typePrefix')}${type}`
}

/**
 * 获取订单状态的Badge类型
 */
export const getOrderStatusBadgeType = (status: number): string => {
  const badgeTypes: Record<number, string> = {
    [ORDER_STATUS_ENUM.DRAFT.value]: 'default',
    [ORDER_STATUS_ENUM.IN_PROGRESS.value]: 'processing',
    [ORDER_STATUS_ENUM.COMPLETED.value]: 'success',
    [ORDER_STATUS_ENUM.CANCELLED.value]: 'danger'
  }
  return badgeTypes[status] || 'default'
}

/**
 * 获取关联方类型的标签
 */
export const getPartyTypeLabel = (type: string): string => {
  const enumItem = Object.values(PARTY_TYPE_ENUM).find(item => item.value === type)
  return enumItem?.label || type
}

/**
 * 获取控件类型的标签
 */
export const getWidgetTypeLabel = (type: number): string => {
  const enumItem = Object.values(WIDGET_TYPE_ENUM).find(item => item.value === type)
  return enumItem?.label
}

/**
 * 订单同步状态枚举
 * @description 订单同步状态：0-未同步 1-同步中 2-已同步 3-同步失败
 */
export const ORDER_SYNC_STATUS_ENUM = {
  NOT_SYNCED: {
    value: 0,
    get label() {
      return t('orders.enum.syncStatus.notSynced')
    },
    color: 'gray'
  },
  SYNCING: {
    value: 1,
    get label() {
      return t('orders.enum.syncStatus.syncing')
    },
    color: 'blue'
  },
  SYNCED: {
    value: 2,
    get label() {
      return t('orders.enum.syncStatus.synced')
    },
    color: 'green'
  },
  SYNC_FAILED: {
    value: 3,
    get label() {
      return t('orders.enum.syncStatus.syncFailed')
    },
    color: 'red'
  },
  PRE_REVIEW_REJECTED: {
    value: 4,
    get label() {
      return t('orders.enum.syncStatus.preReviewRejected')
    },
    color: 'orange'
  }
} as const

/**
 * 获取订单同步状态的标签
 */
export const getOrderSyncStatusLabel = (status?: number): string => {
  if (status === undefined || status === null) {
    return ORDER_SYNC_STATUS_ENUM.NOT_SYNCED.label
  }
  const enumItem = Object.values(ORDER_SYNC_STATUS_ENUM).find(item => item.value === status)
  return enumItem?.label || t('orders.messages.unknown')
}

/**
 * 获取订单同步状态的颜色
 */
export const getOrderSyncStatusColor = (status?: number): string => {
  if (status === undefined || status === null) {
    return ORDER_SYNC_STATUS_ENUM.NOT_SYNCED.color
  }
  const enumItem = Object.values(ORDER_SYNC_STATUS_ENUM).find(item => item.value === status)
  return enumItem?.color || 'gray'
}
